import { CommonModule } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import { Component, computed, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  FormsUtil,
  MbsPopupType,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  SelectComponent,
  ToastService
} from 'mbs-ui-kit';
import { AddCompanyModalComponent } from 'mbs-ui-shared@components/add-company/add-company-modal.component';
import { SelectLicenseParams } from 'mbs-ui-shared@components/components/models/license.model';
import { ChangeLicenseCase } from 'mbs-ui-shared@components/licenses/components/select-license-modal/types';
import { SelectLicenseFormExtendedComponent } from 'mbs-ui-shared@components/select-license-form-extended/select-license-form-extended.component';
import { AddUserModalComponent } from 'mbs-ui-shared@components/users/add-user/add-user-modal.component';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { UsersFacade } from 'mbs-ui-shared@facades/users.facade';
import { Company, LicenseType, PermissionsEnum } from 'mbs-ui-shared@models';
import Computer, { AgentType } from 'mbs-ui-shared@models/Computer';
import { getAgentTypeForLicenseKind, LicenseKind } from 'mbs-ui-shared@models/License';
import { User } from 'mbs-ui-shared@models/user';
import { getLastUrlSegment } from 'mbs-ui-shared@utils/get-url-segment.helper';
import { EMPTY, from, noop, Observable, of, take } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, pairwise, startWith, switchMap, tap } from 'rxjs/operators';
import { AuthorizationLicenseErrorModalComponent } from '../authorization-license-error-modal/authorization-license-error-modal.component';
import { ComputerAuthorizationModalMode } from './computer-authorization-modal.constants';

@UntilDestroy()
@Component({
  selector: 'mbs-computer-authorization-modal',
  templateUrl: './computer-authorization-modal.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    NgbTooltipModule,
    SelectLicenseFormExtendedComponent,
    AlertComponent,
    I18NextPipe,
    ModalComponent,
    CheckboxComponent,
    SelectComponent,
    ModalFooterDirective
  ]
})
export class ComputerAuthorizationModalComponent implements OnInit {
  private companiesFacade = inject(CompaniesFacade);
  private fb = inject(FormBuilder);
  private usersFacade = inject(UsersFacade);
  private modalService = inject(ModalService);
  private computersFacade = inject(ComputersFacade);
  private i18n = inject(I18NextPipe);
  private toastService = inject(ToastService);
  private ability = inject(AbilityService);
  private licensesFacade = inject(LicensesFacade);

  public readonly alertType = MbsPopupType;
  public readonly computerAuthorizationModalMode = ComputerAuthorizationModalMode;
  public readonly permissionsEnum = PermissionsEnum;

  public computers: Computer[];
  public isGroupOperation = false;
  public groupOperationComputersCount$: Observable<number>;
  private readonly fakeCompanyId = '-1';
  private editEffectInitialized = false;

  public modalForm: FormGroup<{
    company: FormControl<Company | null>;
    user: FormControl<User>;
    assignUserManually: FormControl<boolean>;
  }> = this.fb.group({
    company: [null as Company, [Validators.required]],
    user: [null],
    assignUserManually: [false]
  });
  public selectLicenseForm = new FormGroup({
    changeLicenseCase: new FormControl(null),
    selectedLicense: new FormControl(null),
    selectedAnotherPoolLicense: new FormControl(null),
    selectedLicenseType: new FormControl(null)
  });

  private get company(): FormControl<Company> {
    return this.modalForm?.get('company') as FormControl;
  }

  private get user(): FormControl<User> {
    return this.modalForm?.get('user') as FormControl;
  }

  public get assignUserManually(): FormControl<boolean> {
    return this.modalForm?.get('assignUserManually') as FormControl;
  }

  private modalFormIsValid = toSignal(
    this.modalForm.statusChanges.pipe(
      startWith(this.modalForm.status),
      distinctUntilChanged(),
      map((status) => status === 'VALID')
    )
  );
  private selectLicenseFormIsValid = toSignal(
    this.selectLicenseForm.statusChanges.pipe(
      startWith(this.selectLicenseForm.status),
      distinctUntilChanged(),
      map((status) => status === 'VALID')
    )
  );
  public formsAreValid = computed(() => this.modalFormIsValid() && this.selectLicenseFormIsValid());

  private initiated = signal(false);
  public companies = computed(() => [...(this.fakeCompany() ? [this.fakeCompany()] : []), ...this.companiesFacade.dataWhenLoaded()]);
  public users = computed(() => this.usersFacade.dataWhenLoaded());
  public showAssignUserManually = signal(true);
  public selectedCompanyUsers = computed(() => {
    const companyId = this.selectedCompany()?.id;

    return this.users().filter((user) => (companyId === this.fakeCompanyId ? !user?.company : user?.company?.id === companyId));
  });
  public requestInProgress = signal(false);
  public mode = signal(ComputerAuthorizationModalMode.Create);
  public administratorMode = signal(false);
  public showFakeCompany = signal(false);
  public fakeCompany = computed(() =>
    this.showFakeCompany()
      ? ({
          id: this.fakeCompanyId,
          name: `[${this.i18n.transform('shared:computers.modals.userWithoutCompany')}]`
        } as Company)
      : null
  );
  public showRMMPECEAlert = signal(false);

  public selectedCompany = toSignal(this.company?.valueChanges.pipe(startWith(this.company?.value)));
  public showAddUserButton = computed(
    () => this.selectedCompany()?.id !== this.fakeCompanyId && this.ability.can('read', this.permissionsEnum.UsersCreateEdit)
  );

  public get showAddCompanyButton(): boolean {
    return (
      this.ability.can('read', PermissionsEnum.ManageCompanies) &&
      this.ability.can('read', PermissionsEnum.AccessToAllCompanies) &&
      this.ability.can('read', PermissionsEnum.UsersCreateEdit)
    );
  }

  public get authorizeComputersAlertText$(): Observable<string> {
    return this.groupOperationComputersCount$.pipe(
      map(
        (param) =>
          this.i18n.transform('shared:computers.authorizeComputersAlertText', { param }) +
          this.i18n.transform('shared:computers.computer', { count: param })
      )
    );
  }

  public get computer() {
    return this.computers?.[0];
  }

  public get isSelectBuyLicense() {
    return this.selectLicenseForm.get('changeLicenseCase').value === ChangeLicenseCase.BUY;
  }

  public isEdit = computed(() => this.mode() === ComputerAuthorizationModalMode.Edit);

  public get hasBackupAgent() {
    return Computer.hasAgent(this.computer, AgentType.Backup);
  }

  public get hasRMMAgent() {
    return Computer.hasAgent(this.computer, AgentType.RMM);
  }

  public get hasConnectAgent() {
    return Computer.hasAgent(this.computer, AgentType.RA);
  }

  private get isRMMPE() {
    return this.ability.can('read', PermissionsEnum.RmmPerEndpoint);
  }

  private get isLicensedAgentBeta(): boolean {
    return Computer.isBetaAgent(this.computer, getAgentTypeForLicenseKind(this.licenseKind));
  }

  public isAssignLicenseApplicable = computed(
    () =>
      !this.isGroupOperation &&
      !this.isEdit() &&
      (this.hasBackupAgent || ((this.hasRMMAgent || this.hasConnectAgent) && this.isRMMPE)) &&
      !this.computer.isBootableUsb &&
      !this.isLicensedAgentBeta
  );

  public loading = computed(() => {
    const companiesLoading = this.companiesFacade.dataEntityLoading();
    const usersLoading = this.usersFacade.dataEntityLoading();
    return companiesLoading || usersLoading;
  });

  baseModal = viewChild(ModalComponent);

  constructor() {
    effect(() => {
      const loadedCompanies = this.companiesFacade.dataLoaded();
      const loadedUsers = this.usersFacade.dataLoaded();

      if (loadedCompanies && loadedUsers && this.initiated()) {
        if (!this.editEffectInitialized) {
          if (this.isEdit()) {
            this.showAssignUserManually.set(!this.computers[0]?.userAccount?.id);
            this.assignUserManually.setValue(!!this.computers[0]?.userAccount?.id);
            this.company.setValue(this.fakeCompany() ? this.fakeCompany() : this.computers[0]?.company);
            this.updateAssignUserManually();
            this.user.setValue(this.selectedCompanyUsers()?.find((user) => user.id === this.computers[0]?.userAccount?.id) ?? null);
            this.setUserValidators(!!this.computers[0]?.userAccount?.id);
            this.company.markAsDirty();
            this.user.markAsDirty();
          }
          this.editEffectInitialized = true;
          return;
        }

        const hasPreviousCompanyValue = !!this.company?.value?.id;
        const company = this.companies()?.find((company) => company.id === this.company?.value?.id) ?? null;
        const hasNewCompanyValue = !!company;
        const lastUserValue = this.user?.value?.id;
        this.company.setValue(company);
        if (hasPreviousCompanyValue && !hasNewCompanyValue) {
          this.showAssignUserManually.set(true);
          this.assignUserManually.setValue(false);
          this.user.setValue(null);
          this.setUserValidators(false);
        } else {
          this.user.setValue(
            this.assignUserManually.value ? (this.selectedCompanyUsers()?.find((user) => user.id === lastUserValue) ?? null) : null
          );
        }
      }
    });
  }

  ngOnInit(): void {
    this.mode.set(
      !this.isGroupOperation && this.computers.length && (!!this.computers[0]?.company || !!this.computers[0]?.userAccount?.id)
        ? ComputerAuthorizationModalMode.Edit
        : ComputerAuthorizationModalMode.Create
    );

    this.showFakeCompany.set(
      this.isGroupOperation || (this.computers.length && !this.computers[0]?.company && !!this.computers[0]?.userAccount?.id)
    );

    this.companiesFacade.loadData();
    this.usersFacade.loadData();

    this.createFormSubscriptions();
    this.setValidators();

    this.initiated.set(true);
  }

  public get licenseKind(): LicenseKind {
    if (this.computers?.length === 1 && this.isRMMPE) {
      if (Computer.getAgent(this.computers[0], AgentType.Backup)?.online) return LicenseKind.Backup;
      if (Computer.getAgent(this.computers[0], AgentType.RMM)?.online) return LicenseKind.Rmm;
      if (Computer.getAgent(this.computers[0], AgentType.RA)?.online) return LicenseKind.Connect;
    }

    return LicenseKind.Backup;
  }

  public onAddCompanyButtonClick() {
    from(this.modalService.openCustom(AddCompanyModalComponent))
      .pipe(catchError(() => EMPTY))
      .subscribe((company) => {
        this.company.patchValue(company as Company);
        this.company.markAsDirty();
      });
  }

  public onAddUserButtonClick() {
    from(
      this.modalService.openCustom(AddUserModalComponent, {
        data: {
          companyId: this.modalForm.value.company.id,
          hid: !this.isGroupOperation && this.computers.length ? this.computers[0].hid : null
        }
      })
    )
      .pipe(catchError(() => EMPTY))
      .subscribe((user) => {
        this.user.patchValue(user as User);
        this.user.markAsDirty();
      });
  }

  public onSaveButtonClick() {
    if (!this.modalForm.valid) {
      FormsUtil.triggerValidation(this.modalForm);
      return;
    }

    if (!this.isGroupOperation && !this.computers.length) {
      this.toastService.error(this.i18n.transform('shared:computers.incorrectComputerData'));
      return;
    }

    this.showRMMPECEAlert.set(false);

    this.buyLicenseIfSelected()
      .pipe(
        filter(Boolean),
        tap(() => this.requestInProgress.set(true)),
        switchMap(() => {
          const companyId = this.company.value.id === this.fakeCompanyId ? null : this.company.value.id;
          const userId = this.assignUserManually.value ? this.user?.value?.id : null;

          if (!this.isGroupOperation) return this.authorizeComputer(userId, companyId);

          if (this.computers.length)
            return this.computersFacade.groupAuthorizeComputers(
              this.computers.map((computer) => computer.hid),
              userId,
              companyId
            );

          return this.computersFacade.currentPageRequestParams$(true, true).pipe(
            take(1),
            switchMap((filter) => this.computersFacade.bulkAuthorizeComputers(filter, userId, companyId))
          );
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success();
          this.isGroupOperation && this.computersFacade.invalidate();
          !this.isGroupOperation &&
            this.computers.length &&
            this.computersFacade.loadComputerByHid({ hid: this.computers[0].hid, force: true });
          this.computersFacade.checkOneComputerExist(true);
          this.baseModal().save();
        },
        error: (errorDetails) => {
          this.catchAuthorizeComputerError(errorDetails);

          if (errorDetails?.status === HttpStatusCode.NotFound) {
            this.companiesFacade.loadData(true);
            this.usersFacade.loadData(true);
          }

          this.requestInProgress.set(false);
        }
      });
  }

  private authorizeComputer(userId: string, companyId: string) {
    const value = this.selectLicenseForm.value;

    const params: SelectLicenseParams = value.selectedLicenseType
      ? {
          licenseTypeId: value.selectedLicenseType,
          hid: this.computer.hid,
          availableLicenseId: value.selectedAnotherPoolLicense || value.selectedLicense
        }
      : null;

    return this.isEdit() || this.isSelectBuyLicense
      ? this.computersFacade.authorizeComputer(this.computers[0].hid, userId, companyId)
      : this.computersFacade.authorizeComputer(this.computers[0].hid, userId, companyId, params);
  }

  private catchAuthorizeComputerError(error) {
    const licensesErrors = new Set([
      ErrorCode.LicenseError,
      ErrorCode.LicenseNotSet,
      ErrorCode.EndUserNoLicense,
      ErrorCode.LicenceExpired,
      ErrorCode.EndUserLicenseExpired,
      ErrorCode.TrialExpired,
      ErrorCode.LicenseIsNotSupported
    ]);

    const errorCode = Number(getLastUrlSegment(error.error.type));

    if (
      this.isAssignLicenseApplicable() &&
      this.ability.can('read', PermissionsEnum.RmmPerEndpoint) &&
      this.ability.can('read', PermissionsEnum.RmmCeEnabled) &&
      this.selectLicenseForm.value.selectedLicenseType === LicenseType.RmmCePerEndpoint &&
      error?.error?.typeCode === ErrorCode.LicenseLimitExceeded
    ) {
      this.showRMMPECEAlert.set(true);

      return;
    } else if (licensesErrors.has(errorCode)) {
      this.modalService.openCustom(AuthorizationLicenseErrorModalComponent).catch(noop);
    }

    this.baseModal().save();
  }

  private createFormSubscriptions(): void {
    if (this.mode() === ComputerAuthorizationModalMode.Create || !this.computers[0]?.userAccount?.id) {
      this.assignUserManually.valueChanges?.pipe(untilDestroyed(this)).subscribe((value) => {
        const userControl = this.user;
        !value && this.user.patchValue(null);
        this.setUserValidators(value);
        userControl.updateValueAndValidity();
      });
    }

    this.company.valueChanges?.pipe(startWith(null), pairwise(), untilDestroyed(this)).subscribe(([prev, next]) => {
      this.user.patchValue(null);
      prev?.id !== next?.id && this.updateAssignUserManually(next);
    });
  }

  private updateAssignUserManually(company?: Company): void {
    if (!this.showAssignUserManually()) return;

    const companyId = company?.id ?? this.company?.value?.id;

    if (companyId === this.fakeCompanyId) {
      this.assignUserManually.setValue(true);
      this.assignUserManually.disable();
    } else {
      this.assignUserManually.setValue(false);
      this.assignUserManually.enable();
    }
  }

  private setUserValidators(setValidators = true): void {
    setValidators ? this.user.setValidators([Validators.required, this.userEnabled.bind(this)]) : this.user.clearValidators();
    this.user.updateValueAndValidity();
  }

  private userEnabled(control: AbstractControl): ValidationErrors | null {
    const user = this.users().find((user) => control.value?.id === user.id);

    if (!user) return null;

    if (!user.enabled) return { userEnabled: { message: this.i18n.transform('shared:computers.modals.userNotEnabled') } };

    if (user.isDeleting) return { userEnabled: { message: this.i18n.transform('shared:computers.modals.userIsDeleting') } };

    return null;
  }

  private buyLicenseIfSelected(): Observable<boolean> {
    if (!this.isSelectBuyLicense) return of(true);

    const licenseType = this.selectLicenseForm.value.selectedLicenseType;
    return from(this.licensesFacade.proceedWithAutoAssign(licenseType, this.computer.hid)).pipe(map(Boolean));
  }

  private setValidators() {
    if (this.isAssignLicenseApplicable()) {
      this.selectLicenseForm.controls.selectedLicenseType.setValidators([Validators.required]);
    }
  }
}
